import {
  Component,
  ElementRef,
  QueryList,
  ViewChildren,
  ChangeDetectionStrategy
} from "@angular/core";
import { WidgetParent } from "../../class/widget-parent";
import { ComponentAdapterFactory } from "../../class/adapters/component-adapter-factory";
import { SmartPaletteGeneral } from "../../class/smart-palette";
import { ComponentAdapterWeatherStation } from "../../class/adapters/component-adapter-weather-station";
import { DateHelper } from "../../class/general-classes/date-helper";
import { DateTimeFormats } from "../../class/general-classes/date-time-formats";
import { interval, Subscription } from "rxjs";
import { debounceTime, filter } from "rxjs/operators";
import type { Animation } from "@ionic/angular";
import { StaticClass } from "../../class/general-classes/static-classes";
import { IconHelper } from "../../class/icon-helper";
import { IonIcon } from "@ionic/angular";
import { SmartComponentSpinnerComponent } from "../smart-component-spinner/smart-component-spinner.component";
import { ErrorLoadingWidgetComponent } from "../error-loading-widget/error-loading-widget.component";
import { LocalDatePipe } from "../../pipes/local-date.pipe";

@Component({
    selector: "app-widget-weather-station",
    templateUrl: "./widget-weather-station.component.html",
    styleUrls: ["./widget-weather-station.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [IonIcon, SmartComponentSpinnerComponent, ErrorLoadingWidgetComponent, LocalDatePipe]
})
export class WidgetWeatherStationComponent extends WidgetParent<ComponentAdapterWeatherStation> {
  private _timeSub: Subscription = null;
  private _alarmAnimation: Animation;
  private _widgetIsDisplayableSub: Subscription = null;

  public isWindAlarm: boolean = false;
  public isRainAlarm: boolean = false;
  public isNight: boolean = false;
  /** Time of sunrise received from the station, null until the first value */
  public sunriseTime: Date | null = null;
  /** Time of sunset received from the station, null until the first value */
  public sunsetTime: Date | null = null;
  public windSpeedValue: string = "";
  public ambientBrightnessValue: string = "";
  public actualTemperatureValue: string = "";

  public iconHelper = IconHelper;

  @ViewChildren("alarmIcons", { read: ElementRef })
  _alarmIcons: QueryList<ElementRef>;

  public ngAfterViewInit(): void {
    this._widgetIsDisplayableSub = this._widgetIsDisplayable
      .pipe(
        filter((isReady) => isReady),
        debounceTime(500)
      )
      .subscribe((isReady) => {
        //animate alarm icons
        if (this.isRainAlarm || this.isWindAlarm) {
          this.createAlarmAnimation();
        }
      });
  }

  protected setWidgetSmartPalette(): SmartPaletteGeneral {
    let widgetSmaertPalette = new SmartPaletteGeneral();

    return widgetSmaertPalette;
  }

  protected onComponentLoad(): void { }

  protected onSmartComponentErrorLoading(): void { }

  protected onComponentDestroy(): void {
    this._alarmAnimation?.stop();
    this._alarmAnimation = null;
    this._widgetIsDisplayableSub?.unsubscribe();
    this._timeSub?.unsubscribe();
  }

  protected onSmartComponentSuccessLoaded(): void | boolean {
    const factory = new ComponentAdapterFactory();
    this.componentAdapter = factory.createComponentAdapter(
      this._listSmartComponentSkills,
      ComponentAdapterWeatherStation
    );

    this.titleWidget = this.componentAdapter.Name;

    //If component title is empty show error loading component
    if (!this.titleWidget) {
      return (this._errorLoading = true);
    }
  }

  protected onAddressValueChange(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    this.tryToUpdateSunrise(instanceGUID, address, value);
    this.tryToUpdateSunset(instanceGUID, address, value);

    if (this.componentAdapter.RainAlarm) {
      this.tryToUpdateRainAlarm(instanceGUID, address, value);
    }

    if (this.componentAdapter.WindAlarm) {
      this.tryToUpdateWindAlarm(instanceGUID, address, value);
    }

    if (this.componentAdapter.IsDayNight) {
      this.tryToUpdateDayNight(instanceGUID, address, value);
    }

    if (this.componentAdapter.WindSpeed) {
      this.tryToUpdateWindSpeed(instanceGUID, address, value);
    }

    if (this.componentAdapter.AmbientBrightness) {
      this.tryToUpdateAmbientBrightness(instanceGUID, address, value);
    }

    if (this.componentAdapter.ActualTemperature) {
      this.tryToUpdateActualTemperature(instanceGUID, address, value);
    }

    //animate alarm icons
    if (this.isRainAlarm || this.isWindAlarm) {
      setTimeout(() => this.createAlarmAnimation(), 500);
    } else {
      this._alarmAnimation?.stop();
      this._alarmAnimation = null;
    }
  }

  /**
   * Try to test received value and update the day/night value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateDayNight(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.IsDayNight.instanceGUID &&
      address === this.componentAdapter.IsDayNight.address &&
      this.componentAdapter.IsDayValue !== ""
    ) {
      this.isNight = value !== this.componentAdapter.IsDayValue;
    }
  }

  /**
   * Try to test received value and update the rain alarm value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateRainAlarm(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.RainAlarm.instanceGUID &&
      address === this.componentAdapter.RainAlarm.address
    ) {
      this.isRainAlarm = parseInt(value) === 1;
    }
  }

  /**
   * Try to test received value and update the wind alarm value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateWindAlarm(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.WindAlarm.instanceGUID &&
      address === this.componentAdapter.WindAlarm.address
    ) {
      this.isWindAlarm = parseInt(value) === 1;
    }
  }

  /**
   * Try to test received value and update the sunrise value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateSunrise(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.SunriseTime.instanceGUID &&
      address === this.componentAdapter.SunriseTime.address
    ) {
      this.sunriseTime = DateHelper.parse(
        value,
        DateTimeFormats.DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS
      );
    }
  }

  /**
   * Try to test received value and update the sunset value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateSunset(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.SunsetTime.instanceGUID &&
      address === this.componentAdapter.SunsetTime.address
    ) {
      this.sunsetTime = DateHelper.parse(
        value,
        DateTimeFormats.DATETIME_FORMAT_LONG_HOUR_MINUTE_SECONDS
      );
    }
  }

  /**
   * Try to test received value and update the wind speed value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateWindSpeed(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.WindSpeed.instanceGUID &&
      address === this.componentAdapter.WindSpeed.address
    ) {
      this.windSpeedValue = value + this.componentAdapter.WindSpeed?.measureUnit;
    }
  }

  /**
   * Try to test received value and update the ambient brightness value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateAmbientBrightness(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.AmbientBrightness.instanceGUID &&
      address === this.componentAdapter.AmbientBrightness.address
    ) {
      this.ambientBrightnessValue =
        value + this.componentAdapter.AmbientBrightness?.measureUnit;
    }
  }

  /**
   * Try to test received value and update the actual temperature value
   *
   * @param instanceGUID Instance GUID to test
   * @param address address to test
   * @param value Value to apply
   */
  private tryToUpdateActualTemperature(
    instanceGUID: string,
    address: string,
    value: string
  ): void {
    if (
      instanceGUID === this.componentAdapter.ActualTemperature.instanceGUID &&
      address === this.componentAdapter.ActualTemperature.address
    ) {
      this.actualTemperatureValue =
        StaticClass.formatDecimalValue(value, 1, true) +
        this.componentAdapter.ActualTemperature?.measureUnit;
    }
  }

  /**
   * Create animation for alarm icons
   */
  private createAlarmAnimation(): void {
    const icons: Node[] = this._alarmIcons.map((icon) => icon.nativeElement);

    this._alarmAnimation = this._animationController
      .create()
      .addElement(icons)
      .duration(1500)
      .iterations(Infinity)
      .direction("alternate")
      .fromTo("opacity", 1, 0.3);

    this._alarmAnimation.play();
  }
}
